import { type RefObject } from 'react';
import { Button } from 'react-bootstrap';
import { Trans, useTranslation } from 'react-i18next';
import { UploadFileProgress } from 'modules/files';
import spinnerTitle from 'components/others/spinnerTitle';
import AiInfoBlock from 'components/others/AiInfoBlock';

interface UploadDocumentAiPanelProps {
    isBusy: boolean;
    handleOpenClick: () => void;
    processingLabel: string;
    phase: 'idle' | 'uploading' | 'processing';
    handleCancel: () => void;
    uploadProgress: number | boolean;
    refInputFile: RefObject<HTMLInputElement>;
    handleChangeFile: () => void;
}

const UploadDocumentAiPanel = ({
    isBusy,
    handleOpenClick,
    processingLabel,
    phase,
    handleCancel,
    uploadProgress,
    refInputFile,
    handleChangeFile,
}: UploadDocumentAiPanelProps) => {
    const { t } = useTranslation(['Objects']);

    return (
        <div className="px-3">
            <AiInfoBlock title={t('documents.uploadAi.infoTitle')}>
                <p>{t('documents.uploadAi.infoText')}</p>
                <div className="ai-info-block-formats">
                    <span className="ai-info-block-formats-label">{t('documents.uploadAi.formatsLabel')}</span>
                    {t('documents.uploadAi.formatsList')}
                </div>
                <div className="d-flex flex-wrap gap-2 mt-3">
                    <Button type="button" variant="primary" size="sm" onClick={handleOpenClick} disabled={isBusy}>
                        {spinnerTitle(
                            <>
                                <i className="bi bi-stars me-1"></i>
                                {t('documents.uploadAi.open')}
                            </>,
                            isBusy,
                            processingLabel,
                        )}
                    </Button>
                    {phase === 'processing' && (
                        <Button type="button" variant="outline-danger" size="sm" onClick={handleCancel}>
                            <i className="bi bi-stop-circle me-1"></i>
                            {t('documents.uploadAi.cancel')}
                        </Button>
                    )}
                </div>
                <div className="small text-muted mt-2">
                    <Trans
                        i18nKey="documents.uploadAi.editHint"
                        ns="Objects"
                        components={{ strong: <strong /> }}
                    />
                </div>
                {uploadProgress !== false && (
                    <div className="pt-2">
                        <UploadFileProgress progress={Number(uploadProgress)} />
                        <div className="small text-muted mt-2">{processingLabel}</div>
                    </div>
                )}
                <input ref={refInputFile} type="file" className="d-none" onChange={handleChangeFile} />
            </AiInfoBlock>
        </div>
    );
};

export default UploadDocumentAiPanel;
